// Pure helpers for the Cash Back config (asrth/mdf-bo-fn#290 / mdf-bo-api#654).
// Behaviour is specified in tests/unit/cashbackConfig.spec.js; the screens that
// use it are StorefrontSettings.vue (the shop-level card), PromotionBundleEdit.vue
// (the per-bundle override) and PromotionBundles.vue (the list badge).
//
// THE RULE THE WHOLE FEATURE TURNS ON
//
//   A promotion that sets a percentage (> 0) REPLACES the shop default.
//   It does not add to it. 0 is not "zero cashback for this bundle" — it is
//   "this bundle has no opinion", which is exactly the overload bonus_points
//   already carries, kept identical on purpose so an operator learns one rule.
//   And when the master switch is off, the effective percentage is 0 no matter
//   what either number says.
//
// This mirrors services/cashback.EffectivePercent on the API side. The two are
// separate implementations of one rule, which is a real risk — so the rule is
// written here in the same three branches and the same order, and the spec file
// (docs/specs/2026-09-30-cashback-config-design.md §4) is the referee.
//
// NOTHING HERE GIVES A COIN. This round is config only; the customer sees
// nothing until item 6 of the member-app plan turns the coin ledger on.

// The shop-level default the API reports when nothing has been saved yet
// (mdf-bo-api CashbackDefaultPercent). Kept here so a form can render before the
// first GET resolves without flashing a 0 the operator might then save.
export const CASHBACK_DEFAULT_PERCENT = 20;

// normalizeCashbackPercent turns whatever a form, a URL or an API response
// produced into a percentage this feature will accept — or null.
//
// null means "not a usable percentage", and callers are expected to substitute
// their own default rather than silently sending a wrong number: el-input-number
// yields null when its field is cleared, and an empty box must not be read as 0%.
//
// Accepts numbers and numeric strings (an API that serialises numeric(5,2) as a
// string is a real possibility and costs nothing to tolerate). Rejects NaN,
// Infinity, booleans, null/undefined, empty or whitespace-only strings, anything
// below 0 and anything above 100. Rounds to two decimals, the precision the
// column stores.
export function normalizeCashbackPercent(v) {
  if (v === null || v === undefined || typeof v === 'boolean') return null;
  // Only a number or a string is a candidate. Without this gate JavaScript's
  // coercion quietly manufactures percentages out of things that are not one:
  // Number([]) is 0 and Number(['5']) is 5, so an array arriving from a
  // half-deserialised API response would become a valid, saveable setting.
  if (typeof v !== 'number' && typeof v !== 'string') return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return null;
  if (n < 0 || n > 100) return null;
  // Round through the string form, the way the API does
  // (strconv.FormatFloat(p,'f',2,64)), so the two agree at the ties this
  // feature keeps meeting — 12.345 is 12.35 on both sides, not 12.34 on one.
  return Number(n.toFixed(2));
}

// cashbackEffectivePercent answers "what percentage actually applies to this
// bundle right now?" — the number the hint under the form field shows.
//
// enabled: the shop-level master switch.
// def:     the shop-level default percentage.
// promo:   this bundle's override (0 / null / invalid = no override).
//
// Always returns a number in [0, 100]; an unusable input is treated as absent,
// never propagated.
export function cashbackEffectivePercent(enabled, def, promo) {
  if (!enabled) return 0;
  const p = normalizeCashbackPercent(promo);
  if (p !== null && p > 0) return p;
  const d = normalizeCashbackPercent(def);
  if (d !== null && d > 0) return d;
  return 0;
}

// cashbackEffectiveLabel is the same answer packaged for the template: which
// i18n key to render, and the number to interpolate into it.
//
// Returns { key, percent }:
//   cashback_settings.effective_off      — the switch is off; percent 0
//   cashback_settings.effective_promo    — this bundle's own percentage wins
//   cashback_settings.effective_default  — the shop default applies
//
// The three-way split exists because the WORDING has to differ, not just the
// number: "ใช้ค่าของโปรโมชั่นนี้ X%" and "ใช้ค่าร้าน X%" are the two things an
// operator is actually trying to tell apart when they look at this hint, and an
// off switch has to say so loudly enough that nobody reports "cashback is
// broken" about a feature that was never turned on.
export function cashbackEffectiveLabel(enabled, def, promo) {
  if (!enabled) {
    return { key: 'cashback_settings.effective_off', percent: 0 };
  }
  const p = normalizeCashbackPercent(promo);
  if (p !== null && p > 0) {
    return { key: 'cashback_settings.effective_promo', percent: p };
  }
  return {
    key: 'cashback_settings.effective_default',
    percent: cashbackEffectivePercent(enabled, def, promo),
  };
}

// formatCashbackPercent renders a percentage for DISPLAY: no trailing '.00' on a
// whole number, at most two decimals otherwise. The badge on the promotion list
// says "7.5%", not "7.50%", and "20%", not "20.00%".
//
// An unusable value renders as an empty string, so a caller that forgot to guard
// shows nothing rather than "NaN%".
export function formatCashbackPercent(v) {
  const n = normalizeCashbackPercent(v);
  if (n === null) return '';
  return String(Number(n.toFixed(2)));
}

export default normalizeCashbackPercent;
